import React from 'react';
import { useDraggable } from '@dnd-kit/core';
import { AlertTriangle, Edit, FolderInput, Shield } from 'lucide-react';
import clsx from 'clsx';
import DeviceStatusBadge from '../../../components/DeviceStatusBadge';
import DeviceKindIcon from '../../../components/DeviceKindIcon';
import { isVpnHandshakeFresh } from '../../../utils/vpn';

/** Multi-link: chips A/B/C com IP aux e cor pelo handshake do túnel do link. */
function VpnLinkChips({ vpnLinks }) {
  if (!vpnLinks?.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-0.5">
      {vpnLinks.map((l) => {
        const alive = isVpnHandshakeFresh(l.lastHandshake);
        return (
          <span
            key={l.linkRole}
            title={`${l.label || `link ${l.linkRole}`} — ${alive ? 'túnel OK' : 'sem handshake recente'}`}
            className={`inline-flex items-center gap-1 text-[11px] font-mono rounded px-1.5 py-0.5 border ${alive ? 'bg-success-100 text-success-700 border-success-100' : 'bg-error-100 text-error-700 border-error-100'}`}
          >
            <span className="font-bold">{l.linkRole}</span>
            {l.vpnIp}
          </span>
        );
      })}
    </div>
  );
}

/**
 * Linha de um device na tabela. Arrastável (dnd-kit) para soltar sobre um grupo;
 * `depth` só recua o conteúdo quando a linha está dentro de um grupo expandido.
 */
const DeviceRow = ({
  device,
  depth = 0,
  t,
  onOpen,
  onOpenFirewall,
  onMove,
  onContextMenu,
  renderTypeCells,
  renderAccessActions,
  tempAccess,
}) => {
  // Acesso temporário que a nuvem não conseguiu revogar: credencial root/full possivelmente ativa.
  const vulnerable = tempAccess?.status === 'vulnerable';
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `device-${device.id}`,
    data: { type: 'device', deviceId: device.id, groupId: device.managedDeviceGroupId ?? null },
  });

  return (
    <tr
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={() => onOpen(device)}
      onContextMenu={(e) => {
        e.preventDefault();
        onContextMenu(e, device);
      }}
      className={clsx(
        'cursor-pointer select-none',
        vulnerable ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-gray-50',
        isDragging && 'opacity-40'
      )}
    >
      <td
        className={clsx('px-6 py-4', vulnerable && 'border-l-4 border-red-500')}
        style={depth > 0 ? { paddingLeft: `${24 + depth * 24}px` } : undefined}
      >
        <div className="flex items-start gap-3">
          <div
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-600 mt-0.5"
            aria-hidden
          >
            <DeviceKindIcon
              kind={device.kind}
              kindIconFileName={device.kindIconFileName}
              customIconBase64={device.customIconBase64}
              size={18}
            />
          </div>
          <div className="min-w-0">
            <div className="font-medium text-gray-900">{device.name}</div>
            {device.description && <div className="text-sm text-gray-500">{device.description}</div>}
            {device.vpnIp && <div className="text-sm text-gray-500 font-mono">{device.vpnIp}</div>}
            <VpnLinkChips vpnLinks={device.vpnLinks} />
            {vulnerable && (
              <div
                className="mt-1 flex items-center gap-1 text-xs font-semibold text-red-700"
                title={tempAccess.lastRevokeError || ''}
              >
                <AlertTriangle size={13} className="shrink-0" />
                {t('managedDevices.tempAccess.rowVulnerable', { user: tempAccess.username })}
              </div>
            )}
          </div>
        </div>
      </td>
      <td className="px-6 py-4">
        <DeviceStatusBadge status={device.status} />
      </td>
      <td className="px-6 py-4">
        <span className="text-sm text-gray-700">{device.kindName || device.kind}</span>
      </td>
      {renderTypeCells(device)}
      <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-end gap-1 w-full">
          {renderAccessActions(device)}
          <button
            onClick={() => onMove(device)}
            title={t('managedDevices.groups.moveToGroup')}
            className="p-1 text-gray-600 hover:bg-gray-50 rounded"
          >
            <FolderInput size={14} />
          </button>
          <button
            onClick={() => onOpenFirewall(device)}
            title="Firewall"
            className="p-1 text-theme-accent hover:bg-theme-accent/10 rounded"
          >
            <Shield size={14} />
          </button>
          <button
            onClick={() => onOpen(device)}
            title={t('common.buttons.edit')}
            className="p-1 text-gray-600 hover:bg-gray-50 rounded"
          >
            <Edit size={14} />
          </button>
        </div>
      </td>
    </tr>
  );
};

export default DeviceRow;
